// Procedures (E01-S3-T3; contracts 1.0 section 3): Sop, SopStep, Flow, the engine's events and
// evidence, and the walkthrough state the engine keeps. These schemas judge one step at a time;
// graph rules (reachability, termination, dangling targets) are E03-S4-T1's.
import { z } from "zod";
import { addDuplicateIssues } from "./atlas.js";
import { Citation } from "./citation.js";
import { ExprField } from "./expr/schema.js";
import { Count, Id, IsoUtc, NonEmpty, strictObject, Ulid, ULID_RE, version } from "./primitives.js";
import { WalkthroughOutcome, WalkthroughRef } from "./record.js"; // the WP-XXXXXX grammar (D-80) is stated once, there
/** Who content is written for. */
export const Audience = z.enum(["associate", "manager", "administrator"]);
/** A calendar date, YYYY-MM-DD, that exists. */
export const IsoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD")
    .refine((s) => {
    const [y, m, d] = s.split("-").map(Number);
    const t = new Date(Date.UTC(y, m - 1, d));
    return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}, "not a real date");
export const OpenLoopSpec = strictObject({
    id: Id,
    text: NonEmpty,
    why: NonEmpty,
    citation: Citation,
});
/** A watch-out is a string, which inherits the step's citation, or text with its own (D-79). */
export const WatchOut = z.union([NonEmpty, strictObject({ text: NonEmpty, citation: Citation })]);
export const FirmRule = strictObject({ label: NonEmpty, value: NonEmpty, source: Citation });
export const StepQuestion = strictObject({
    prompt: NonEmpty,
    branches: z
        .array(strictObject({ id: Id, label: NonEmpty, hint: NonEmpty.optional(), nextStepId: Id }))
        .min(2, "a question offers at least two branches"),
    infer: z.array(strictObject({ when: ExprField, branchId: Id, because: NonEmpty })).optional(),
}).superRefine((q, ctx) => {
    addDuplicateIssues(q.branches, ["branches"], "branch", ctx);
    const ids = new Set(q.branches.map((b) => b.id));
    (q.infer ?? []).forEach((r, i) => {
        if (!ids.has(r.branchId))
            ctx.addIssue({
                code: "custom",
                message: `infer names branch ${r.branchId}, which this question does not offer`,
                path: ["infer", i, "branchId"],
            });
    });
});
const isPlain = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
const stepName = (v) => (isPlain(v) && typeof v["id"] === "string" ? v["id"] : "(no id)");
export const SopStep = strictObject({
    id: Id,
    ordinal: z.number().int().positive(),
    title: NonEmpty,
    instruction: NonEmpty,
    location: NonEmpty.optional(),
    citation: Citation,
    firmRules: z.array(FirmRule).optional(),
    watchOuts: z.array(WatchOut).optional(),
    // how the step ends: exactly one of question / nextStepId / terminal
    expect: ExprField.optional(),
    question: StepQuestion.optional(),
    nextStepId: Id.optional(),
    terminal: z.enum(["disposition", "escalation"]).optional(),
    dispositionCode: NonEmpty.optional(),
    dispositionNote: NonEmpty.optional(),
    escalateTo: NonEmpty.optional(),
    openLoop: OpenLoopSpec.optional(),
    // how the step is shown
    anchor: strictObject({ screen: Id.optional(), overlay: Id.optional(), target: Id }).optional(),
    beat: Id.optional(),
    say: NonEmpty.optional(),
})
    // Runs even when other issues exist, so a missing citation is reported against the step's id.
    // `when` runs it on input that failed the shape, so the argument is read as unknown.
    .refine((s) => !isPlain(s) || s["citation"] !== undefined, {
    when: () => true,
    path: ["citation"],
    error: (iss) => `step ${stepName(iss.input)}: a citation is mandatory`,
})
    .superRefine((s, ctx) => {
    const bad = (path, message) => {
        ctx.addIssue({ code: "custom", message: `step ${s.id}: ${message}`, path: [path] });
    };
    const endings = [
        s.question !== undefined,
        s.nextStepId !== undefined,
        s.terminal !== undefined,
    ].filter(Boolean).length;
    if (endings !== 1)
        bad(endings === 0 ? "nextStepId" : "terminal", `a step ends in exactly one of question, nextStepId or terminal (found ${String(endings)})`);
    if (s.terminal === "disposition" && s.dispositionCode === undefined)
        bad("dispositionCode", "a disposition step names its dispositionCode");
    if (s.terminal !== "disposition" &&
        (s.dispositionCode !== undefined || s.dispositionNote !== undefined))
        bad("dispositionCode", "dispositionCode and dispositionNote belong to a disposition step");
    if (s.terminal === "escalation" && s.escalateTo === undefined)
        bad("escalateTo", "an escalation step names escalateTo");
    if (s.terminal !== "escalation" && s.escalateTo !== undefined)
        bad("escalateTo", "escalateTo belongs to an escalation step");
    if (s.openLoop !== undefined && s.terminal === undefined)
        bad("openLoop", "an open loop belongs to a terminal step");
    if (s.anchor?.screen !== undefined && s.anchor.overlay !== undefined)
        bad("anchor", "an anchor is on a screen or an overlay, not both");
});
export const Sop = strictObject({
    v: version(1),
    id: Id,
    title: NonEmpty,
    version: NonEmpty,
    effectiveDate: IsoDate,
    reviewDate: IsoDate.optional(),
    owner: NonEmpty,
    approver: NonEmpty,
    appliesTo: strictObject({
        classes: z.array(NonEmpty),
        reasonCodes: z.array(NonEmpty),
        screens: z.array(Id).optional(),
    }),
    audience: z.array(Audience).min(1),
    relatedTraining: Id.optional(),
    scope: NonEmpty,
    firstStepId: Id,
    steps: z.array(SopStep).min(1),
}).superRefine((s, ctx) => {
    addDuplicateIssues(s.steps, ["steps"], `step in ${s.id}:`, ctx);
});
export const Flow = strictObject({
    v: version(1),
    id: Id,
    title: NonEmpty,
    legs: z
        .array(strictObject({
        sopId: Id,
        fromStepId: Id.optional(),
        untilStepId: Id.optional(),
        when: ExprField.optional(),
    }))
        .min(1),
});
// ── what the engine is given and what it keeps (D-17) ──────────────────────
/** `<stepId>.expect` or `<stepId>.infer.<n>`. */
export const EVIDENCE_KEY_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}\.(?:expect|infer\.(?:0|[1-9]\d*))$/;
export const EvidenceKey = z
    .string()
    .regex(EVIDENCE_KEY_RE, "expected <stepId>.expect or <stepId>.infer.<n>");
export const Evidence = strictObject({
    seq: Count,
    at: IsoUtc,
    truths: z.record(EvidenceKey, z.boolean()),
});
/** An open loop's id is scoped to its walkthrough: `<walkthroughId>:<loopId>` (D-59). */
export const SCOPED_LOOP_ID_RE = new RegExp(`^${ULID_RE.source.slice(1, -1)}:[A-Za-z0-9][A-Za-z0-9._-]{0,127}$`);
export const ScopedLoopId = z
    .string()
    .regex(SCOPED_LOOP_ID_RE, "expected <walkthroughId>:<loopId>");
export const EngineEvent = z.discriminatedUnion("t", [
    // ADR-026: `continues` names the walkthrough this one carries on from (a hand-off, or the next
    // leg of a flow), so the record's "Continues WP-…" entry replays from the event log (D-80).
    strictObject({
        t: z.literal("start"),
        evidence: Evidence.optional(),
        continues: WalkthroughRef.optional(),
    }),
    strictObject({ t: z.literal("ack") }),
    strictObject({ t: z.literal("choose"), branchId: Id, how: z.literal("asked") }),
    strictObject({ t: z.literal("evidence"), evidence: Evidence }),
    strictObject({ t: z.literal("stop") }),
    strictObject({ t: z.literal("resume"), evidence: Evidence.optional() }),
    strictObject({ t: z.literal("restart") }),
    strictObject({ t: z.literal("loopDone"), loopId: z.union([Id, ScopedLoopId]) }),
]);
export const WalkthroughState = strictObject({
    v: version(1),
    walkthroughId: Ulid,
    ref: WalkthroughRef,
    sop: strictObject({ id: Id, version: NonEmpty }),
    currentStepId: Id.nullable(),
    completed: z.array(strictObject({
        stepId: Id,
        how: z.enum(["verified", "acknowledged", "passed"]),
        at: IsoUtc,
        seq: Count.optional(),
    })),
    decisions: z.array(strictObject({
        stepId: Id,
        branchId: Id,
        how: z.enum(["asked", "inferred"]),
        because: NonEmpty.optional(),
        at: IsoUtc,
    })),
    outcome: WalkthroughOutcome.optional(),
    openLoops: z.array(strictObject({ id: ScopedLoopId, text: NonEmpty, done: z.boolean() })),
}).superRefine((w, ctx) => {
    addDuplicateIssues(w.completed.map((c) => ({ id: c.stepId })), ["completed"], "completed step", ctx, "stepId");
    addDuplicateIssues(w.decisions.map((d) => ({ id: d.stepId })), ["decisions"], "decision for step", ctx, "stepId");
    addDuplicateIssues(w.openLoops, ["openLoops"], "open loop", ctx);
});
//# sourceMappingURL=sop.js.map